import { env } from "@/env";
import { Link } from "@components/core/Link";
import { cn } from "@ui/lib/utils";
import { cva } from "class-variance-authority";
import Script from "next/script";
import { Fragment } from "react";

type Links = { href?: null | string; title: string }[] | null;

const breadcrumbLinkVariants = cva(
  "tw-flex tw-items-center tw-justify-center tw-px-1 tw-py-0.5 tw-rounded-lg tw-whitespace-nowrap tw-transition-colors",
  {
    variants: {
      isLast: {
        true: "",
        false: "",
      },
      size: {
        default: "tw-text-base tw-leading-[1.4]",
        small: "tw-text-sm tw-leading-[1.4]",
      },
      inverse: {
        true: "",
        false: "",
      },
    },
    compoundVariants: [
      { isLast: false, inverse: false, class: "tw-text-black/60 hover:tw-bg-gray-100/50" },
      { isLast: true, inverse: false, class: "tw-text-black" },
      { isLast: false, inverse: true, class: "tw-text-white/60 hover:tw-bg-white/10" },
      { isLast: true, inverse: true, class: "tw-text-white" },
    ],
    defaultVariants: {
      isLast: false,
      size: "default",
      inverse: false,
    },
  },
);

const breadcrumbItemVariants = cva("tw-flex tw-items-center tw-whitespace-nowrap", {
  variants: {
    isLast: {
      true: "tw-h-0 tw-w-0 tw-overflow-hidden lg:tw-h-auto lg:tw-w-auto",
      false: "",
    },
    size: {
      default: "",
      small: "",
    },
    inverse: {
      true: "",
      false: "",
    },
  },
  defaultVariants: {
    isLast: false,
    size: "default",
    inverse: false,
  },
});

const BlockBreadcrumbsV2 = ({
  links,
  size,
  inverse = false,
  hidenOnMobileAfter = 1,
}: {
  links?: Links;
  size?: "default" | "small";
  inverse?: boolean;
  hidenOnMobileAfter?: number;
}) => {
  if (!links?.length) {
    return null;
  }

  return (
    <>
      <Script
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              links
                .filter(link => !!link.href)
                .map((link, index) => ({
                  "@type": "ListItem",
                  position: index + 1,
                  name: link.title,
                  item: env.NEXT_PUBLIC_SERVER_URL + link.href,
                })),
            ],
          }),
        }}
        id='breadcrumbs'
        strategy='lazyOnload'
        type='application/ld+json'
      />
      <ul className='tw-flex tw-items-center tw-justify-start tw-gap-0.5 tw-overflow-x-auto'>
        {links.map((link, index) => {
          const isFirst = index === 0;
          // hide last item (usually unnecessary current page) on smaller screens
          const isLast = index === links.length - 1;
          const shouldHideOnMobile = index > hidenOnMobileAfter;

          const component =
            link.href && !isLast ? (
              <Link
                {...link}
                className={cn(breadcrumbLinkVariants({ isLast, size, inverse }))}
                data-last={isLast}
                href={link.href}
                key={link.href}
                variant='clean'
              >
                {link.title}
              </Link>
            ) : (
              <span
                className={cn(breadcrumbLinkVariants({ isLast: true, size, inverse }), "disabled:tw-cursor-default")}
                data-last={true}
                key={index}
              >
                {link.title}
              </span>
            );

          return (
            <Fragment key={index}>
              {!isFirst ? (
                <li
                  aria-hidden='true'
                  role='presentation'
                  className={cn(
                    "tw-text-base tw-leading-[1.4] tw-whitespace-nowrap",
                    inverse ? "tw-text-white/60" : "tw-text-black/60",
                    shouldHideOnMobile && "tw-hidden lg:tw-inline-block",
                  )}
                >
                  /
                </li>
              ) : null}
              <li className={cn(breadcrumbItemVariants({ isLast: shouldHideOnMobile, size, inverse }))}>{component}</li>
            </Fragment>
          );
        })}
      </ul>
    </>
  );
};

BlockBreadcrumbsV2.displayName = "BlockBreadcrumbsV2";

export { BlockBreadcrumbsV2 };
